import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';
import { getItemTemplate } from '@shared/data/items';

/**
 * Ventanita del MODO TRABAJO (auto-extracción). Mientras trabajás un nodo (yacimiento/árbol/
 * cardumen) muestra: ícono del recurso + nombre + cuánto tenés (inventario) + cuánto le queda al
 * nodo. Se abre con WORK_STARTED y se cierra con WORK_STOPPED. Phaser, filtrado a la uiCamera.
 * Ver docs/FISHING_SKILL.md.
 */
export class WorkProgressPanel {
  private readonly scene: Phaser.Scene;
  private parts: Phaser.GameObjects.GameObject[] = [];
  private isOpen = false;

  private bg!: Phaser.GameObjects.Rectangle;
  private icon!: Phaser.GameObjects.Sprite;
  private nameText!: Phaser.GameObjects.Text;
  private haveText!: Phaser.GameObjects.Text;
  private remainText!: Phaser.GameObjects.Text;

  private readonly W = 220;
  private readonly H = 54;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    const px = Math.floor((scene.scale.width - this.W) / 2);
    const py = 64;

    this.bg = this.track(
      scene.add.rectangle(px, py, this.W, this.H, 0x1c1a17, 0.95).setOrigin(0, 0).setStrokeStyle(2, 0x6b5a3e).setDepth(1150).setScrollFactor(0)
    );
    this.icon = this.track(
      scene.add.sprite(px + 26, py + this.H / 2, 'item_icon_noicon').setDisplaySize(32, 32).setDepth(1151).setScrollFactor(0)
    );
    this.nameText = this.track(
      scene.add.text(px + 50, py + 10, '', { fontFamily: GAME_FONT_FAMILY, fontSize: '14px', color: '#e8c98a', resolution: 2 }).setDepth(1151).setScrollFactor(0)
    );
    this.haveText = this.track(
      scene.add.text(px + 50, py + 28, '', { fontFamily: GAME_FONT_FAMILY, fontSize: '12px', color: '#cfc8b8', resolution: 2 }).setDepth(1151).setScrollFactor(0)
    );
    this.remainText = this.track(
      scene.add.text(px + this.W - 10, py + 28, '', { fontFamily: GAME_FONT_FAMILY, fontSize: '12px', color: '#8fb7cf', resolution: 2 }).setOrigin(1, 0).setDepth(1151).setScrollFactor(0)
    );

    const main = scene.cameras.main;
    if (main) main.ignore(this.parts);
    this.setVisible(false);
  }

  private track<T extends Phaser.GameObjects.GameObject>(o: T): T {
    this.parts.push(o);
    return o;
  }

  private setVisible(v: boolean): void {
    for (const p of this.parts) (p as unknown as { setVisible?: (b: boolean) => void }).setVisible?.(v);
  }

  private iconKey(itemId: string): string {
    const tpl = getItemTemplate(itemId);
    const key = tpl ? `item_icon_${tpl.icon}` : 'item_icon_noicon';
    return this.scene.textures.exists(key) ? key : 'item_icon_noicon';
  }

  /** Abre la ventanita para un recurso (nombre + ícono del item que produce). */
  public show(producesItemId: string, resourceName: string): void {
    this.icon.setTexture(this.iconKey(producesItemId)).setDisplaySize(32, 32);
    this.nameText.setText(resourceName);
    this.haveText.setText('Tenés: 0');
    this.remainText.setText('');
    this.isOpen = true;
    this.setVisible(true);
  }

  /** Actualiza los contadores en vivo (llamar desde el update loop mientras trabajás). */
  public update(held: number, remaining: number, max: number): void {
    if (!this.isOpen) return;
    this.haveText.setText(`Tenés: ${held}`);
    this.remainText.setText(`Quedan: ${remaining}/${max}`);
  }

  public hide(): void {
    this.isOpen = false;
    this.setVisible(false);
  }

  public isVisible(): boolean {
    return this.isOpen;
  }
}
